'use client';

import React, { useEffect, useCallback, useState } from 'react';
import { keysToCamelFromSnake } from '../../utils/caseconverters';
import Base from '../../layouts/Base';
import Section from '../../components/Section/Section';
import { t } from '../../i18n';

import s from './MediaMonitorPage.module.scss';
import AuthorList from '../../components/AuthorList/AuthorList';
import AccordionFade from '../../components/AccordionFade/AccordionFade';
import Richtext from '../../components/Richtext';
import MediaMonitorFilter from '../../components/MediaMonitorFilter/MediaMonitorFilter';
import MediaMonitorFilterGraph from '../../components/MediaMonitorFilterGraph/MediaMonitorFilterGraph';
import ArticleCard from '../../components/ArticleCard/ArticleCard';
import classNames from 'classnames';
import TagList from '../../components/TagList/TagList';
import StreamField from '../../components/StreamField';
import Grid from '../../components/Grid/Grid';
import Button from '../../components/Button/Button';
import SkeletonLoader from '../../components/SkeletonLoader/SkeletonLoader';
import { useIntersectionObserver } from '../../utils/useIntersectionObserver';
import Bookmark from '../../components/Bookmark/Bookmark';
import {
    usePathname,
    useSearchParams,
    useRouter,
    redirect,
} from 'next/navigation';
import { useArticlePagination } from './hooks/useArticlePagination';

const MediaMonitorPage = ({
    articles = { items: [], hasMore: false, nextPage: null },
    searchArticles = { items: [], hasMore: false, nextPage: null },
    mostReadArticles = [],
    content = {},
    type = 'landing',
    rootUrl,
    searchUrl,
    sortAndSortUrl,
    filters = {},
    graphs = [],
}) => {
    const hasGraphs = !!graphs && graphs.length > 0;
    const useGraphLayout = hasGraphs;

    if (type === 'detail' && !content?.title && rootUrl) {
        redirect(rootUrl);
    }

    return (
        <div className={`${s.Root} ${useGraphLayout ? s.RootWithGraphs : ''}`}>
            <main className={s.Main}>
                <div className={s.Content}>
                    <Section noMarginBottom isMainSection modifier="White">
                        {useGraphLayout ? (
                            <GraphMediaMonitor
                                graphs={graphs}
                                articles={articles}
                                searchArticles={searchArticles}
                                type={type}
                                content={content}
                                filters={filters}
                                searchUrl={searchUrl}
                                rootUrl={rootUrl}
                                sortAndSortUrl={sortAndSortUrl}
                            />
                        ) : (
                            <OldMediaMonitor
                                articles={articles}
                                searchArticles={searchArticles}
                                mostReadArticles={mostReadArticles}
                                content={content}
                                type={type}
                                rootUrl={rootUrl}
                                searchUrl={searchUrl}
                                sortAndSortUrl={sortAndSortUrl}
                                filters={filters}
                            />
                        )}
                    </Section>
                </div>
            </main>
        </div>
    );
};

const OldMediaMonitor = ({
    articles,
    searchArticles,
    mostReadArticles,
    content,
    type,
    rootUrl,
    searchUrl,
    sortAndSortUrl,
    filters,
}) => {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const currentSearchParams = searchParams.toString()
        ? `?${searchParams.toString()}`
        : '';
    const [isQueryLoading, setIsQueryLoading] = useState(false);
    const [highlightedWords, setHighlightedWords] = useState([]);
    const [mostReadPages, setMostReadPages] = useState(mostReadArticles);
    const [isMostReadLoading, setIsMostReadLoading] = useState(true);

    const mmId = articles?.objectId ?? articles?.object_id ?? null;
    const { pages, hasMore, isLoading, onLoadMore } = useArticlePagination(
        articles?.items,
        articles?.hasMore,
        articles?.nextPage,
        mmId
    );

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const query = params.get('q');
        if (query) setHighlightedWords(query.split(','));
    }, []);

    useEffect(() => {
        setMostReadPages(mostReadArticles);
    }, [mostReadArticles]);

    useEffect(() => {
        if (!mmId) {
            setIsMostReadLoading(false);
            return;
        }

        const controller = new AbortController();
        setIsMostReadLoading(true);

        async function loadMostReadArticles() {
            try {
                const response = await fetch(
                    `/wt/fenix/media-monitor/most-read/?mm=${encodeURIComponent(mmId)}`,
                    { signal: controller.signal }
                );

                if (!response.ok) {
                    throw new Error(
                        `Failed to load most-read articles (${response.status})`
                    );
                }

                const data = keysToCamelFromSnake(await response.json());
                setMostReadPages(Array.isArray(data) ? data : []);
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Error loading most-read articles:', error);
                    setMostReadPages([]);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setIsMostReadLoading(false);
                }
            }
        }

        loadMostReadArticles();

        return () => {
            controller.abort();
        };
    }, [mmId]);

    const handleQueryLoadingChange = (isLoading) => {
        setIsQueryLoading(isLoading);
    };

    return (
        <>
            {type !== 'detail' && (
                <div className={s.TopWrapper}>
                    <div className={s.Filter}>
                        <MediaMonitorFilter
                            filters={filters}
                            searchUrl={searchUrl}
                            rootUrl={rootUrl}
                            onQueryLoadingChange={handleQueryLoadingChange}
                        />

                        {type !== 'landing' && (
                            <div className={s.BackButton}>
                                <Button
                                    title={t('watchtowerPage.clearAll')}
                                    modifier="Dark"
                                    url={rootUrl}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
            <div className={s.LayoutWrapper}>
                <div className={s.Left}>
                    {type === 'detail' && (
                        <div className={s.ArticleTopBar}>
                            <div className={s.ArticleTopBarLeft}>
                                <TagList tags={content?.tags || []} />
                            </div>
                            <div className={s.ArticleTopBarRight}>
                                <Bookmark
                                    url={pathname}
                                    modifiers={['Grey']}
                                    isSmall={false}
                                    categories={['Media monitor']}
                                    alternativeTitle={content?.title || ''}
                                />
                                <Button
                                    title={t('mediaMonitorPage.closeArticle')}
                                    modifier="Secondary"
                                    onClick={() => {
                                        const params = new URLSearchParams(
                                            currentSearchParams
                                        );
                                        params.delete('id');
                                        const qs = params.toString();
                                        window.location.href = qs
                                            ? `${rootUrl}?${qs}`
                                            : rootUrl;
                                    }}
                                />
                            </div>
                        </div>
                    )}
                    {type === 'landing' && (
                        <div className={s.Container}>
                            <ArticleList
                                pages={pages}
                                onLoadMore={onLoadMore}
                                hasMore={hasMore}
                                isLoading={isLoading}
                            />
                        </div>
                    )}

                    {type === 'search' && (
                        <>
                            <div className={s.SearchResultCount}>
                                {isQueryLoading ? (
                                    <div className={s.SkeletonLoaderText}>
                                        <SkeletonLoader size={'cover'} />
                                    </div>
                                ) : (
                                    <div className={s.SearchResultTextWrapper}>
                                        <div className={s.SearchLeft}>
                                            <span
                                                className={s.SearchResultText}>
                                                {t('mediaMonitorPage.weFound')}
                                                <span
                                                    className={
                                                        s.SearchResultNumber
                                                    }>
                                                    {searchArticles.total ??
                                                        searchArticles.items
                                                            ?.length ??
                                                        0}
                                                </span>
                                                {t('mediaMonitorPage.articles')}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div
                                className={classNames(s.Container, [
                                    s[`Container--SearchResultList`],
                                ])}>
                                <SearchResultList
                                    searchWords={highlightedWords}
                                    articles={searchArticles.items ?? []}
                                    hasMore={searchArticles.hasMore}
                                    nextPage={searchArticles.nextPage}
                                    mmId={mmId}
                                    isQueryLoading={isQueryLoading}
                                />
                            </div>
                        </>
                    )}

                    {type === 'detail' && content?.title && (
                        <div
                            className={classNames(s.Container, [
                                s[`Container--ArticleList`],
                            ])}>
                            <ArticleContent {...content} hideTags />
                        </div>
                    )}
                </div>
                <div className={s.Right}>
                    <div className={s.Container}>
                        <>
                            <span className={s.Label}>
                                {' '}
                                {t('mediaMonitorPage.mostRead')}
                            </span>
                            {isMostReadLoading ? (
                                <div className={s.SkeletonLoader}>
                                    <SkeletonLoader size={'cover'} />
                                    <SkeletonLoader size={'cover'} />
                                </div>
                            ) : mostReadPages.length > 0 ? (
                                <ArticleList
                                    pages={mostReadPages.map((p) => ({
                                        ...p,
                                        isActive:
                                            !!content?.url &&
                                            p.url === content.url,
                                    }))}
                                    size="Small"
                                    hasMore={false}
                                    onLoadMore={null}
                                />
                            ) : (
                                <p>No articles read</p>
                            )}
                        </>
                    </div>
                </div>
            </div>
        </>
    );
};

const GraphMediaMonitor = ({
    graphs,
    articles,
    searchArticles = { items: [], hasMore: false, nextPage: null },
    type,
    content,
    filters,
    searchUrl,
    rootUrl,
    sortAndSortUrl,
}) => {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const router = useRouter();
    const [isQueryLoading, setIsQueryLoading] = useState(false);
    const [highlightedWords, setHighlightedWords] = useState([]);
    const [prevSearchParams, setPrevSearchParams] = useState(
        searchParams.toString()
    );
    const currentSearchParams = searchParams.toString()
        ? `?${searchParams.toString()}`
        : '';
    const mmId = articles.objectId ?? articles.object_id;

    const { pages, hasMore, isLoading, onLoadMore } = useArticlePagination(
        articles.items,
        articles.hasMore,
        articles.nextPage,
        mmId
    );

    useEffect(() => {
        const currentParams = searchParams.toString();
        if (currentParams !== prevSearchParams) {
            setPrevSearchParams(currentParams);
            router.refresh();
        }
        const query = searchParams.get('q');
        if (query) {
            setHighlightedWords(query.split(','));
        } else {
            setHighlightedWords([]);
        }
    }, [searchParams, prevSearchParams, router]);

    const handleQueryLoadingChange = (isLoading) => {
        setIsQueryLoading(isLoading);
    };

    const hasSearchFilters = (searchArticles.items?.length ?? 0) > 0;

    return (
        <div className={s.LayoutWrapper}>
            <div className={s.Left}>
                {type === 'detail' && (
                    <div className={s.ArticleTopBar}>
                        <div className={s.ArticleTopBarLeft}>
                            <TagList tags={content?.tags || []} />
                        </div>
                        <div className={s.ArticleTopBarRight}>
                            <Bookmark
                                url={pathname}
                                modifiers={['Grey']}
                                isSmall={false}
                                categories={['Media monitor']}
                                alternativeTitle={content?.title || ''}
                            />
                            <Button
                                title={t('mediaMonitorPage.closeArticle')}
                                modifier="Secondary"
                                onClick={() => {
                                    const params = new URLSearchParams(
                                        currentSearchParams
                                    );
                                    params.delete('id');
                                    const qs = params.toString();
                                    window.location.href = qs
                                        ? `${rootUrl}?${qs}`
                                        : rootUrl;
                                }}
                            />
                        </div>
                    </div>
                )}
                <div className={s.Container}>
                    {type === 'detail' ? (
                        content?.title ? (
                            <ArticleContent {...content} hideTags />
                        ) : null
                    ) : (
                        graphs.map((graph, index) => (
                            <div className={s.GraphSection} key={index}>
                                {!!graph.value.title && (
                                    <h2 className={s.GraphTitle}>
                                        {graph.value.title}
                                    </h2>
                                )}
                                <Grid
                                    key={index}
                                    columns={graph.value.gridSize}>
                                    <StreamField
                                        smallPadding
                                        noWrapper={true}
                                        items={graph.value.content}
                                    />
                                </Grid>
                            </div>
                        ))
                    )}
                </div>
            </div>
            <div className={s.Right}>
                <div className={s.Container}>
                    <span className={s.Label}>
                        {t('mediaMonitorPage.newsFeed')}
                    </span>
                    <div className={s.TopWrapper}>
                        <div className={s.Filter}>
                            <MediaMonitorFilterGraph
                                filters={filters}
                                rootUrl={rootUrl}
                                isGraph={true}
                                searchUrl={searchUrl}
                                onQueryLoadingChange={handleQueryLoadingChange}
                            />
                        </div>
                    </div>
                    {type === 'search' || hasSearchFilters ? (
                        <>
                            <div className={s.SearchResultCount}>
                                {isQueryLoading ? (
                                    <div className={s.SkeletonLoaderText}>
                                        <SkeletonLoader size={'cover'} />
                                    </div>
                                ) : (
                                    <div className={s.SearchResultTextWrapper}>
                                        <div className={s.SearchLeft}>
                                            <span
                                                className={s.SearchResultText}>
                                                {t('mediaMonitorPage.weFound')}
                                                <span
                                                    className={
                                                        s.SearchResultNumber
                                                    }>
                                                    {searchArticles.total ??
                                                        searchArticles.items
                                                            ?.length ??
                                                        0}
                                                </span>
                                                {t('mediaMonitorPage.articles')}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <SearchResultList
                                searchWords={highlightedWords}
                                articles={searchArticles.items ?? []}
                                hasMore={searchArticles.hasMore}
                                nextPage={searchArticles.nextPage}
                                mmId={mmId}
                                isQueryLoading={isQueryLoading}
                                searchParams={currentSearchParams}
                            />
                        </>
                    ) : type === 'detail' && hasSearchFilters ? (
                        <>
                            <span className={s.Label}>
                                {t('mediaMonitorPage.searchResults')}
                            </span>
                            <SearchResultList
                                searchWords={highlightedWords}
                                articles={searchArticles.items ?? []}
                                hasMore={searchArticles.hasMore}
                                nextPage={searchArticles.nextPage}
                                mmId={mmId}
                                isQueryLoading={isQueryLoading}
                                searchParams={currentSearchParams}
                            />
                        </>
                    ) : (
                        <ArticleList
                            pages={pages}
                            size="Small"
                            onLoadMore={onLoadMore}
                            hasMore={hasMore}
                            isLoading={isLoading}
                            searchParams={currentSearchParams}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

const ArticleList = ({
    pages,
    size,
    onLoadMore,
    hasMore,
    isLoading,
    searchParams = '',
}) => {
    const observerTarget = useIntersectionObserver(
        () => {
            if (hasMore && !isLoading && onLoadMore) {
                onLoadMore();
            }
        },
        {
            threshold: 0.1,
            rootMargin: '100px',
        }
    );

    const classes = classNames(s.ArticleList, [s[`ArticleList--${size}`]]);

    return (
        <div className={classes}>
            {pages.map((page, index) => (
                <div
                    className={s.ArticleItem}
                    key={index}
                    ref={
                        hasMore && onLoadMore && index === pages.length - 1
                            ? observerTarget
                            : null
                    }>
                    <ArticleCard
                        {...page}
                        author={page.authors?.[0]}
                        useShortSummary={true}
                        size={size}
                        searchParams={searchParams}
                        modifiers={!size ? ['MediaMonitorPage'] : []}
                    />
                </div>
            ))}
            {hasMore && isLoading && (
                <div className={s.SkeletonLoader}>
                    <SkeletonLoader size={'cover'} />
                    <SkeletonLoader size={'cover'} />
                </div>
            )}
        </div>
    );
};

const SEARCH_OBSERVER_OPTIONS = { threshold: 0.1, rootMargin: '100px' };

const SearchResultList = ({
    articles,
    searchWords,
    isQueryLoading,
    searchParams = '',
    hasMore: initialHasMore = false,
    nextPage: initialNextPage = null,
    mmId = null,
}) => {
    const { pages, hasMore, isLoading, onLoadMore } = useArticlePagination(
        articles,
        initialHasMore,
        initialNextPage,
        mmId
    );

    const observerTarget = useIntersectionObserver(() => {
        if (hasMore && !isLoading) {
            onLoadMore();
        }
    }, SEARCH_OBSERVER_OPTIONS);

    return (
        <div className={s.SearchResultList}>
            {isQueryLoading ? (
                <div className={s.SkeletonLoader}>
                    <SkeletonLoader size={'cover'} />
                    <SkeletonLoader size={'cover'} />
                    <SkeletonLoader size={'cover'} />
                    <SkeletonLoader size={'cover'} />
                </div>
            ) : (
                <>
                    {pages.map((article, index) => (
                        <div
                            className={s.SearchResultItem}
                            key={index}
                            ref={
                                hasMore && index === pages.length - 1
                                    ? observerTarget
                                    : null
                            }>
                            <ArticleCard
                                {...article}
                                author={article.authors?.[0]}
                                size="Medium"
                                searchWords={searchWords}
                                searchParams={searchParams}
                            />
                        </div>
                    ))}
                    {hasMore && isLoading && (
                        <div className={s.SkeletonLoader}>
                            <SkeletonLoader size={'cover'} />
                            <SkeletonLoader size={'cover'} />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

const ArticleContent = ({
    publishedDate,
    updatedDate,
    title,
    subtitle,
    authors,
    sources = [],
    accordion,
    richText,
    tags = [],
    bullets,
    hideTags = false,
}) => {
    const isMultipleSources = sources.length > 1;

    return (
        <div className={s.ArticleContent}>
            <div className={s.ArticleTop}>
                <div className={s.ArticleByline}>
                    <span
                        className={
                            s.ArticlePublished
                        }>{`${t('reportArticlePage.published')} ${publishedDate}`}</span>
                    {!!updatedDate && (
                        <span
                            className={
                                s.ArticleUpdated
                            }>{`${t('reportArticlePage.updated')} ${updatedDate}`}</span>
                    )}
                </div>
                {!hideTags && (
                    <div className={s.ArticleTags}>
                        <TagList tags={tags} />
                    </div>
                )}
            </div>
            <h2 className={s.ArticleTitle}>{title}</h2>
            {/* <p className={s.ArticlePreamble}>{subtitle}</p> */}
            <div className={s.ArticleDivider} />
            <div className={s.ArticleAuthors}>
                <AuthorList authors={authors} />
            </div>
            {richText?.text && (
                <div className={s.ArticleRichText}>
                    <Richtext text={richText.text} modifier="ArticleRichText" />
                </div>
            )}
            <Richtext text={bullets} modifier="ArticleBullets" />
        </div>
    );
};

MediaMonitorPage.propTypes = {};

export default Base(MediaMonitorPage);
